import React, { useState } from "react";
import { IBusinessLinePackage } from "@/interfaces/Ibilling";
import {
  FiAlertCircle,
  FiArrowRight,
  FiArrowLeft,
  FiClock,
  FiInfo,
  FiTrendingUp,
  FiTrendingDown,
  FiAlertTriangle,
} from "react-icons/fi";

interface BoughtPackagesCardProps {
  packages: IBusinessLinePackage[];
  onSeeAllPlans?: () => void;
}

const BoughtPackagesCard: React.FC<BoughtPackagesCardProps> = ({
  packages,
  onSeeAllPlans,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextPackage = () => {
    setCurrentIndex((prev) => (prev + 1) % packages.length);
  };

  const prevPackage = () => {
    setCurrentIndex((prev) => (prev - 1 + packages.length) % packages.length);
  };

  const goToPackage = (index: number) => {
    setCurrentIndex(index);
  };

  const formatDeactivationDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZoneName: "short",
    });
  };

  if (!packages || packages.length === 0) {
    return (
      <div className="flex-1 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-lg p-8 flex flex-col min-w-[320px] items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertCircle className="mx-auto text-5xl mb-4 text-gray-400" />
          <p className="text-lg font-medium">No packages found</p>
          <p className="text-sm text-gray-400 mt-1">
            You haven't purchased any packages yet
          </p>
        </div>
      </div>
    );
  }

  const currentPackage = packages[currentIndex];

  // Safety check to ensure currentPackage exists and has required properties
  if (!currentPackage || !currentPackage.package) {
    return (
      <div className="flex-1 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl shadow-lg p-8 flex flex-col min-w-[320px] items-center justify-center">
        <div className="text-center text-gray-500">
          <FiAlertCircle className="mx-auto text-5xl mb-4 text-gray-400" />
          <p className="text-lg font-medium">Invalid package data</p>
          <p className="text-sm text-gray-400 mt-1">
            Package information is incomplete or corrupted
          </p>
        </div>
      </div>
    );
  }

  const usagePercentage =
    (currentPackage.used_minutes / currentPackage.package.minutes) * 100;

  // Determine colors based on usage percentage
  const getColorScheme = (percentage: number) => {
    if (percentage > 66.67) {
      return {
        background: "from-red-50 to-red-100",
        border: "border-red-200",
        progressBar: "bg-red-200",
        progressFill: "bg-red-500",
      };
    } else if (percentage > 33.33) {
      return {
        background: "from-yellow-50 to-yellow-100",
        border: "border-yellow-200",
        progressBar: "bg-yellow-200",
        progressFill: "bg-yellow-500",
      };
    } else {
      return {
        background: "from-emerald-50 to-emerald-100",
        border: "border-emerald-200",
        progressBar: "bg-emerald-200",
        progressFill: "bg-emerald-500",
      };
    }
  };

  const colorScheme = getColorScheme(usagePercentage);

  return (
    <div className="flex-1 flex flex-col min-w-[320px]">
      {/* Main Card */}
      <div
        className={`w-96 h-40 relative bg-gradient-to-br ${colorScheme.background} rounded-lg overflow-hidden shadow-lg border ${colorScheme.border}`}
      >
        {/* Decorative rotated gradient element */}
        <div className="w-72 h-96 left-[452.24px] top-[103.50px] absolute origin-top-left rotate-[162.28deg] bg-gradient-to-bl from-emerald-400/0 to-emerald-200" />

        {/* Price - Top Right */}
        <div className="left-[301px] top-[12px] absolute justify-center text-gray-700 text-xl font-bold font-['Inter']">
          ${currentPackage.package.price.toFixed(2)}
        </div>

        {/* Package Name and Details - Top Left */}
        <div className="left-[25px] top-[12px] absolute text-right justify-center text-gray-700 text-base font-medium font-['Inter']">
          {currentPackage.package.name} - {currentPackage.package.minutes}{" "}
          minutes
        </div>

        {/* Phone Number */}
        <div className="left-[25px] top-[39px] absolute text-right justify-center text-zinc-700 text-base font-normal font-['Inter']">
          {currentPackage.phone_number}
        </div>

        {/* Progress Bar Background */}
        <div
          className={`w-96 h-1.5 left-[25px] top-[123px] absolute ${colorScheme.progressBar} rounded`}
        />

        {/* Progress Bar Fill */}
        <div
          className={`w-64 h-1.5 left-[25px] top-[123px] absolute ${colorScheme.progressFill} rounded transition-all duration-500 ease-out`}
          style={{ width: `${Math.min(usagePercentage, 100) * 2.56}px` }}
        />

        {/* Usage Information */}
        <div className="left-[25px] top-[133px] absolute justify-center text-zinc-600 text-xs font-normal font-['Inter']">
          {currentPackage.call_count} Calls ({currentPackage.used_minutes} mins/
          {currentPackage.package.minutes} mins)
        </div>
      </div>

      {/* Pending Updated Package Section */}
      {currentPackage.pending_updated_package && (
        <div className="mt-3 w-96 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <FiClock className="text-blue-600 text-lg" />
            <span className="text-blue-800 font-semibold text-sm">
              Pending Plan Update
            </span>
            {/* Upgrade/Downgrade Indicator */}
            {currentPackage.pending_updated_package.minutes >
            currentPackage.package.minutes ? (
              <FiTrendingUp
                className="text-green-600 text-lg"
                title="Upgrade"
              />
            ) : currentPackage.pending_updated_package.minutes <
              currentPackage.package.minutes ? (
              <FiTrendingDown
                className="text-red-600 text-lg"
                title="Downgrade"
              />
            ) : null}
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-blue-700 text-sm font-medium">
                {currentPackage.pending_updated_package.name}
              </span>
              <span className="text-blue-800 font-bold text-sm">
                ${currentPackage.pending_updated_package.price.toFixed(2)}
              </span>
            </div>

            <div className="flex justify-between items-center text-xs text-blue-600">
              <span>
                {currentPackage.pending_updated_package.minutes} minutes
                {currentPackage.pending_updated_package.minutes >
                currentPackage.package.minutes ? (
                  <span className="text-green-600 ml-1">
                    (+
                    {currentPackage.pending_updated_package.minutes -
                      currentPackage.package.minutes}
                    )
                  </span>
                ) : currentPackage.pending_updated_package.minutes <
                  currentPackage.package.minutes ? (
                  <span className="text-red-600 ml-1">
                    (-
                    {currentPackage.package.minutes -
                      currentPackage.pending_updated_package.minutes}
                    )
                  </span>
                ) : null}
              </span>
              <span className="flex items-center space-x-1">
                <FiInfo className="text-blue-500" />
                <span>Effective next billing cycle</span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Pending Deactivation Section */}
      {currentPackage.pending_deactivation_date && (
        <div className="mt-3 w-96 bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200 p-4 shadow-sm">
          <div className="flex items-center space-x-2 mb-3">
            <FiAlertTriangle className="text-red-600 text-lg" />
            <span className="text-red-800 font-semibold text-sm">
              Pending Deactivation
            </span>
          </div>

          <div className="space-y-2">
            <div className="text-red-700 text-sm">
              This package will be deactivated on{" "}
              <span className="font-medium">
                {formatDeactivationDate(
                  currentPackage.pending_deactivation_date
                )}
              </span>
            </div>
            <div className="text-red-600 text-xs">
              No changes or deletions are allowed for packages with pending
              deactivation.
            </div>
          </div>
        </div>
      )}

      {/* Navigation - Outside Colored Box */}
      <div className="mt-4 flex justify-between">
        <div className="flex items-center justify-between mb-4">
          {/* Left Arrow */}
          <button
            onClick={prevPackage}
            className="p-2.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={packages.length <= 1}
            aria-label="Previous package"
            title="Previous package"
          >
            <FiArrowLeft className="text-xl" />
          </button>

          {/* Pagination Dots */}
          <div className="flex space-x-2">
            {packages.map((_, index) => (
              <button
                key={index}
                onClick={() => goToPackage(index)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  index === currentIndex
                    ? "bg-emerald-600 scale-110"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                aria-label={`Go to package ${index + 1}`}
                title={`Go to package ${index + 1}`}
              />
            ))}
          </div>

          {/* Right Arrow */}
          <button
            onClick={nextPackage}
            className="p-2.5 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={packages.length <= 1}
            aria-label="Next package"
            title="Next package"
          >
            <FiArrowRight className="text-xl" />
          </button>
        </div>

        {/* See All Plans Link */}
        <div className="text-center">
          <button
            onClick={onSeeAllPlans}
            className="text-emerald-600 hover:text-emerald-800 text-sm font-semibold flex items-center justify-center space-x-2 mx-auto p-2 rounded-lg hover:bg-emerald-50 transition-all duration-200"
          >
            <span>See all plans</span>
            <FiArrowRight className="text-sm" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoughtPackagesCard;
